import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { Button, Dialog, EmptyState, Field, Icon, IconButton, Select, Spinner, useToast } from "@agentsaas/ui";
import type { Notification } from "@agentsaas/protocol";
import { useErrorText, useT } from "../app/i18n";
import { useSession } from "../app/session";
import { formatRelative } from "../app/format";
import { useMarkRead, useNotificationPrefs, useNotifications, useSetPref, useUserNotifications } from "../data/notifications";
import { PushDeviceCard } from "../pwa/PushDeviceCard";
import { useServices } from "../app/services";
import { CATEGORY_ICON, NOTIFY_CATEGORIES, NOTIFY_PREFS, renderNotification, safeLink, type NotifyCategory, type NotifyPref } from "./model";

/** 通知文案渲染（t + key 是否存在） */
export function useRenderNotification() {
  const { t, i18n } = useT();
  return useCallback((n: { code: string; params: Record<string, unknown> }) => renderNotification(t, n, (k) => i18n.exists(k)), [t, i18n]);
}

/**
 * 顶栏铃铛：未读数角标 + 通知列表（手机上是底部 sheet）。
 * 订阅 user:<uid>：新通知到达时刷新列表，并在前台弹 toast（带"查看"跳转）。
 */
export function NotificationBell() {
  const { t } = useT();
  const user = useSession().state.user;
  const [open, setOpen] = useState(false);
  const list = useNotifications(!!user);
  const render = useRenderNotification();
  const toast = useToast();
  const navigate = useNavigate();
  const go = useOpenNotification(() => setOpen(false));

  useUserNotifications(user?.id, (n) => {
    if (open) return;
    const r = render(n);
    toast.show({ message: `${r.title}: ${r.body}`, duration: 5000, action: { label: t("notify.open_list"), onClick: () => setOpen(true) } });
  });

  return (
    <>
      <IconButton
        icon="bell"
        label={t("notify.bell", { count: list.unread })}
        badge={list.unread}
        onClick={() => setOpen(true)}
        data-testid="notify-bell"
      />
      <Dialog open={open} onClose={() => setOpen(false)} title={t("notify.title")} closeLabel={t("shell.close")}>
        <NotificationList list={list} onOpen={(n) => void go(n)} onSettings={() => (setOpen(false), navigate("/settings#notifications"))} />
      </Dialog>
    </>
  );
}

/** 点击通知：标记已读 → 站内跳转 */
function useOpenNotification(close: () => void) {
  const navigate = useNavigate();
  const markRead = useMarkRead();
  return async (n: Notification) => {
    close();
    if (!n.read) void markRead([n.id]).catch(() => {});
    const link = safeLink(n.link);
    if (link) navigate(link);
  };
}

function NotificationList({ list, onOpen, onSettings }: { list: ReturnType<typeof useNotifications>; onOpen(n: Notification): void; onSettings(): void }) {
  const { t, locale } = useT();
  const render = useRenderNotification();
  const markRead = useMarkRead();
  const toast = useToast();
  const errText = useErrorText();
  const [now] = useState(() => Date.now());
  const onReadAll = () => void markRead("all").catch((e) => toast.show({ message: errText(e), tone: "danger" }));
  return (
    <div className="notify" data-testid="notify-list">
      <div className="notify__bar">
        <span className="grow small muted">{t("notify.unread_count", { count: list.unread })}</span>
        <Button size="sm" variant="ghost" icon="check" disabled={!list.unread} onClick={onReadAll} data-testid="notify-read-all">
          {t("notify.mark_all")}
        </Button>
        <IconButton icon="settings" label={t("notify.settings")} onClick={onSettings} data-testid="notify-settings" />
      </div>
      {list.loading ? (
        <Spinner label={t("common.loading")} />
      ) : list.items.length ? (
        <ul className="list notify__items">
          {list.items.map((n) => {
            const r = render(n);
            return (
              <li key={n.id}>
                <button type="button" className={`list-item notify-item${n.read ? "" : " is-unread"}`} onClick={() => onOpen(n)} data-testid="notify-item" data-read={n.read}>
                  <span className="notify-item__icon">
                    <Icon name={r.category ? CATEGORY_ICON[r.category] : "bell"} />
                  </span>
                  <span className="notify-item__main">
                    <span className="notify-item__title">{r.title}</span>
                    <span className="notify-item__body small">{r.body}</span>
                    <time className="small muted" dateTime={n.createdAt}>
                      {formatRelative(n.createdAt, locale, now)}
                    </time>
                  </span>
                  {n.read ? null : <span className="notify-item__dot" aria-label={t("notify.unread")} role="img" />}
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <EmptyState icon="bell" title={t("notify.empty")} />
      )}
      {list.hasMore ? (
        <Button variant="ghost" block loading={list.loadingMore} onClick={list.loadMore}>
          {t("notify.load_more")}
        </Button>
      ) : null}
    </div>
  );
}

/** 设置页：各类通知的送达方式（推送 / 仅站内 / 关闭）+ 本设备推送 */
export function NotificationPrefsSection() {
  const { t } = useT();
  // 原生壳里推送由 App 管理（APNs / FCM），不显示 Web Push 开关
  const { native } = useServices();
  const prefs = useNotificationPrefs();
  const setPref = useSetPref();
  const toast = useToast();
  const errText = useErrorText();
  const onChange = (category: NotifyCategory, pref: NotifyPref) =>
    setPref.mutate({ category, pref }, { onError: (e) => toast.show({ message: errText(e), tone: "danger" }) });
  return (
    <section className="card stack notify-prefs" id="notifications" aria-labelledby="notify-prefs-title" data-testid="notify-prefs">
      <h2 id="notify-prefs-title" className="section-title">
        {t("notify.prefs.title")}
      </h2>
      <p className="small muted">{t("notify.prefs.hint")}</p>
      {native ? null : <PushDeviceCard />}
      {prefs.isPending ? (
        <Spinner label={t("common.loading")} />
      ) : prefs.data ? (
        <div className="notify-prefs__grid">
          {NOTIFY_CATEGORIES.map((c) => (
            <Field key={c} label={t(`notify.category.${c}`)}>
              {(id) => (
                <Select id={id} value={prefs.data[c] ?? "inbox"} onChange={(e) => onChange(c, e.target.value as NotifyPref)} data-testid={`pref-${c}`}>
                  {NOTIFY_PREFS.map((p) => (
                    <option key={p} value={p}>
                      {t(`notify.pref.${p}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ))}
        </div>
      ) : (
        <p className="form-error">{errText(prefs.error)}</p>
      )}
    </section>
  );
}
